/**
 * Column ⋮ filter: resolving WHAT A COLUMN IS, from the metamodel (#959).
 *
 * This is the console's half of the seam. `@solum/ui` owns the operators, the
 * inputs, the applied state and the clearing; it is handed a
 * `ColumnFilterField` and never asks where one came from. Resolving one is a
 * READ — `describe_semantics` for the dictionary, `core.status_machine` for a
 * lifecycle — and reads belong to the app.
 *
 * ── the operator set is decided by the FIELD, never by its name ────────────
 * `core.field_meta.kind` is the metamodel already saying what a column is:
 * text · email · phone · country · number · money · percent · date · datetime ·
 * boolean · ref · option · uuid · json. Every one of those maps to a filter
 * kind here, and the two that do not describe a filterable value (`uuid` as a
 * bare identifier, `json`) map to `unknown`, which the editor answers with the
 * conservative set AND a sentence saying why. A column with no `field_meta` row
 * at all — the generic browser over an entity nobody has described — lands in
 * the same place, which is the honest answer rather than a guess from the name.
 *
 * ── a status column gets the STATUS MACHINE's states ───────────────────────
 * A lifecycle is data (core 0046): `core.status_state` rows, renamable by a
 * tenant, ordered by the machine. Where a machine governs this entity's column,
 * ITS states are the vocabulary — so a deployment that renamed
 * `ready_to_verify` to "QA" filters on "QA" without a deploy. Where there is no
 * machine, `field_meta.options` (itself possibly a picklist catalog, core 0022)
 * is the vocabulary. Where there is neither, the column is `option` with nothing
 * in it, and the editor says so instead of drawing an empty select.
 */
import { operatorsFor, type ColumnFilterField, type ColumnFilterKind, type ColumnFilterOption } from '@solum/ui'
import { governedLeafSupported, type EntityReadCapabilities } from '@solum/sdk'
import type { StatusMachine } from '../data/queries'

/** One `core.field_meta` row, as `describe_semantics` returns it. */
export interface FieldMetaLike {
  column_name: string
  label?: string | null
  kind?: string | null
  /**
   * `unknown[]`, because that is what `describe_semantics` is typed as and what
   * it really is: `[{value,label}]` for a static picklist, and whatever a
   * picklist CATALOG's columns are named for a sourced one (core 0022). Narrowed
   * where it is read, not where it is declared — a declaration that promises
   * more than the RPC does is a lie the compiler helps you tell.
   */
  options?: unknown[] | null
}

/**
 * `field_meta.kind` → the filter kind.
 *
 * `country` keeps text operators deliberately: it is an ISO code with a picker
 * on the FORM, but the dictionary does not ship the code list to a filter, and
 * an `option` kind with no options is a worse experience than "contains".
 */
const KIND: Record<string, ColumnFilterKind> = {
  text: 'text', email: 'email', phone: 'phone', country: 'country',
  number: 'number', money: 'money', percent: 'percent',
  date: 'date', datetime: 'datetime',
  boolean: 'boolean', option: 'option', ref: 'ref',
  // Neither is a value a person filters by comparison. `uuid` here is the bare
  // identifier kind — a REFERENCE is `ref`, and that is the one that gets a picker.
  uuid: 'unknown', json: 'unknown',
}

export function filterKindOf(meta: FieldMetaLike | undefined): ColumnFilterKind {
  return KIND[String(meta?.kind ?? '')] ?? 'unknown'
}

/**
 * The kind resolver `filtersToPostgrest` takes — so the SAME dictionary that
 * decides which operators are offered decides which SQL operator family runs.
 *
 * These being one function is the point: `ilike` on a numeric column is a 400
 * from PostgREST, and the way that ships is the menu deciding a column is a
 * number while the translator assumes text.
 */
export function kindResolver(fields: Map<string, FieldMetaLike>): (field: string) => string | undefined {
  return (field: string) => {
    const k = filterKindOf(fields.get(field))
    return k === 'unknown' ? undefined : k
  }
}

/**
 * The vocabulary for an option column: the status machine's states where one
 * governs this column, else the dictionary's own options.
 */
function optionsFor(column: string, meta: FieldMetaLike | undefined, machine: StatusMachine | null | undefined): ColumnFilterOption[] | undefined {
  if (machine && (machine.statusColumn ?? 'status') === column && machine.states?.length) {
    return machine.states.map((st) => ({ value: st.code, label: st.label || st.code }))
  }
  const opts = (meta?.options ?? []).flatMap((o) => {
    if (o == null || typeof o !== 'object') return []
    const r = o as Record<string, unknown>
    // `value`/`label` is the registered shape; `code` is what a catalog-sourced
    // picklist calls the same thing. An entry with neither is not an option.
    const value = r.value ?? r.code
    if (value == null || value === '') return []
    return [{ value: String(value), label: String(r.label ?? value) }]
  })
  return opts.length ? opts : undefined
}

/**
 * Put `incoming` on `column`, keeping every OTHER column's leaves.
 *
 * Named and exported rather than inlined at the call site, so the property has
 * somewhere to be tested: a filter menu that APPENDED would build
 * `status = paid AND status = void` — a view that can never match — and offer
 * it as an ordinary second click on the same column.
 */
export function replaceColumnFilters<L extends { field?: string }>(existing: L[], column: string, incoming: L[]): L[] {
  return [...existing.filter((f) => f.field !== column), ...incoming]
}

/** Words for a narrowed menu — for a person, so no table, door or operator code. */
export const SUBJECT_FILTER_NARROWED =
  'Only exact matches can be applied: these records are read through a governed read that records every record it returns, and it cannot apply other conditions.'
export const SUBJECT_FILTER_NONE =
  'This column cannot be filtered here: these records are read through a governed read that records every record it returns, and it matches exact values only.'
/** The same, for a record type its MODULE reads through its own governed read (#2759). */
export const MODULE_FILTER_NARROWED =
  "Only the conditions this record type's module offers can be applied: its records are read through the module's own governed read."
export const MODULE_FILTER_NONE =
  "This column cannot be filtered here: its records are read through their module's own governed read, which offers no condition on it."

/**
 * The same column, for a SUBJECT entity (#2444 slice 4): the menu offers only
 * the operators the governed door answers FAITHFULLY for this kind — decided by
 * `subjectLeafSupported`, the very predicate the list's translation
 * (`leavesToSubjectFilters`) applies, so the menu cannot offer what the list
 * would then refuse. `between` is never offered (it is two range leaves).
 * The editor is told why, and a column with nothing left says so.
 */
export function subjectFilterField(field: ColumnFilterField, capabilities: EntityReadCapabilities | null = null): ColumnFilterField {
  const kind = field.kind === 'unknown' ? undefined : field.kind
  const allowedOps = operatorsFor(field).map((o) => o.op)
    .filter((op) => governedLeafSupported(op, kind, field.key, capabilities))
  return {
    ...field,
    allowedOps,
    restrictionReason: capabilities
      ? (allowedOps.length > 0 ? MODULE_FILTER_NARROWED : MODULE_FILTER_NONE)
      : (allowedOps.length > 0 ? SUBJECT_FILTER_NARROWED : SUBJECT_FILTER_NONE),
  }
}

/**
 * Resolve one column into the descriptor `<ColumnFilterEditor>` takes.
 *
 * `refEntity` comes from the surface's own `ListConfig.refs` — the same map that
 * already resolves a reference column's DISPLAY label — because the dictionary
 * records that a column is a `ref` without recording what it points AT.
 */
export function resolveColumnFilterField(a: {
  column: string
  fields: Map<string, FieldMetaLike>
  machine?: StatusMachine | null
  refs?: Record<string, string>
}): ColumnFilterField {
  const meta = a.fields.get(a.column)
  const kindFromMeta = filterKindOf(meta)
  const options = optionsFor(a.column, meta, a.machine)
  /* A column the dictionary calls text, that a status machine nonetheless
     governs, IS an option column — the machine is the stronger statement, and it
     is the one a tenant can change. */
  const kind: ColumnFilterKind = options && kindFromMeta !== 'ref' && kindFromMeta !== 'option'
    && a.machine && (a.machine.statusColumn ?? 'status') === a.column
    ? 'option'
    : kindFromMeta
  return {
    key: a.column,
    label: String(meta?.label ?? a.column),
    kind,
    ...(options ? { options } : {}),
    ...(kind === 'ref' && a.refs?.[a.column] ? { refEntity: a.refs[a.column] } : {}),
  }
}
